// Contrast utilities (pure functions)
import { DataScenario } from 'types/cx-editor';

/**
 * Finds the default scenario ID for a given productType and templateName
 * from the data scenarios array.
 * 
 * @param dataScenarios - Array of data scenarios from the API
 * @param productType - The product type (e.g., "Email")
 * @param templateName - The template name (e.g., "EMAIL.SF.MONTHLY_SUMMARY")
 * @returns The scenario ID of the default scenario, or null if not found
 */
export function findDefaultScenarioId(
  dataScenarios: DataScenario[],
  productType: string,
  templateName: string
): string | null {
  const matchesTemplate = (scenario: DataScenario) =>
    scenario.productType === productType && scenario.templateName === templateName;

  // 1) Preferred: explicit defaultScenario flag
  const defaultByFlag = dataScenarios.find(
    (scenario) => matchesTemplate(scenario) && scenario.defaultScenario === true
  );
  if (defaultByFlag?.scenarioId) return defaultByFlag.scenarioId;

  // 2) Fallback: pick the first scenario for the template
  const firstMatch = dataScenarios.find(matchesTemplate);
  return firstMatch?.scenarioId || null;
}

/**
 * Resolves a scenario value to the actual scenario ID.
 * If the scenario is "default", it fetches data scenarios and finds the default scenario ID.
 * Otherwise, returns the scenario value as-is.
 * 
 * @param scenario - The scenario value (e.g., "default", "MS_1", "MS_2")
 * @param templateKey - The template key (e.g., "EMAIL.SF.MONTHLY_SUMMARY")
 * @param channel - The channel/product type (e.g., "Email", "HER", "Web", "SMS")
 * @param pilot_id - The pilot ID to filter data scenarios
 * @param fuel_type - The fuel type to filter data scenarios (e.g., "ELECTRIC")
 * @param locale - The locale to filter data scenarios (e.g., "en_US")
 * @param getDataScenarios - Function to fetch data scenarios from the API
 * @returns Promise resolving to the actual scenario ID
 */
export async function resolveScenarioId(
  scenario: string,
  templateKey: string,
  channel: string,
  pilot_id: string,
  fuel_type: string,
  locale: string,
  getDataScenarios: (
    pilot_id: string,
    fuel_type: string,
    locale: string,
    signal?: AbortSignal
  ) => Promise<{ dataScenarios: DataScenario[] }>,
  signal?: AbortSignal
): Promise<string> {
  // If scenario is not "default", return it as-is
  if (scenario !== 'default') {
    return scenario;
  }
  
  // Map channel to productType
  const productType = channel || 'Email';
  
  try {
    const response = await getDataScenarios(pilot_id, fuel_type, locale, signal);
    const defaultScenarioId = findDefaultScenarioId(
      response.dataScenarios,
      productType,
      templateKey
    );
    
    // If we found a default scenario ID, return it; otherwise return "default" as fallback
    return defaultScenarioId || scenario;
  } catch (error) {
    console.error('[resolveScenarioId] Failed to fetch data scenarios:', error);
    // Return "default" as fallback if API call fails
    return scenario;
  }
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

export function getLuminance(r: number, g: number, b: number): number {
  const a = [r, g, b].map(v => {
    const n = v / 255;
    return n <= 0.03928 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

export function getContrastRatio(hex1: string, hex2: string): number | null {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  if (!rgb1 || !rgb2) return null;
  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
}









